import { useCallback, useMemo } from 'react';
import { usePlayerContext } from '../PlayerContext';
import { useStore } from '../hooks/useStore';
import { QualityIcon, BackQualityIcon, QualityRombIcon } from '../icons';
import type { QualityLevel } from '../../core/types';
import styles from './QualityMenu.module.css';

const POSITIONAL_LABELS = ['Исходное', 'Высокое', 'Среднее', 'Мобильное'];

function buildMaps(qualities: QualityLevel[]) {
  const labels = new Map<string, string>([['auto', 'Авто']]);
  const nonAuto = qualities
    .filter(q => q !== 'auto')
    .slice()
    .sort((a, b) => (parseInt(b, 10) || 0) - (parseInt(a, 10) || 0));
  for (let i = 0; i < nonAuto.length; i++) {
    labels.set(nonAuto[i], POSITIONAL_LABELS[i] ?? nonAuto[i]);
  }
  return { labels };
}

interface QualityMenuProps {
  /** 'row' — shows only the trigger row inside the main menu (default) */
  mode?: 'row' | 'submenu';
  /** Called when the quality row is clicked in 'row' mode to open the submenu */
  onOpenSubmenu?: () => void;
  /** Called when the back row is clicked in 'submenu' mode */
  onBack?: () => void;
  /** Called after a quality level is selected */
  onAction?: () => void;
}

export function QualityMenu({ mode = 'row', onOpenSubmenu, onBack, onAction }: QualityMenuProps) {
  const { store, eventBus } = usePlayerContext();
  const qualities = useStore(store, useCallback(s => s.availableQualities, []));
  const quality = useStore(store, useCallback(s => s.quality, []));

  const { labels, sorted } = useMemo(() => {
    const { labels } = buildMaps(qualities);
    const sorted: QualityLevel[] = [
      ...(qualities.includes('auto') ? ['auto' as QualityLevel] : []),
      ...qualities
        .filter(q => q !== 'auto')
        .slice()
        .sort((a, b) => (parseInt(b, 10) || 0) - (parseInt(a, 10) || 0)),
    ];
    return { labels, sorted };
  }, [qualities]);

  const handleSelect = useCallback((level: QualityLevel) => {
    eventBus.emit('quality:change', { level, auto: level === 'auto' });
    onAction?.();
  }, [eventBus, onAction]);

  if (qualities.length <= 1) return null;

  if (mode === 'submenu') {
    return (
      <div className={styles.quality}>
        <div
          className={`${styles.row} ${styles.backRow}`}
          role="button"
          tabIndex={0}
          onClick={onBack}
          onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onBack?.()}
        >
          <span className={styles.icon}><BackQualityIcon /></span>
          <span className={styles.label}><span className={styles.labelText}>Качество</span></span>
        </div>
        <ul className={styles.list}>
          {sorted.map(q => (
            <li
              key={q}
              className={`${styles.item}${q === quality ? ` ${styles.active}` : ''}`}
              onClick={() => handleSelect(q)}
            >
              <span className={styles.itemIcon}>{q === quality && <QualityRombIcon />}</span>
              <span className={styles.itemLabel}>{labels.get(q) || q}</span>
              <span className={styles.iconSlot} />
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // mode === 'row': trigger row only, no sub-list
  return (
    <div
      className={styles.row}
      role="button"
      tabIndex={0}
      onClick={onOpenSubmenu}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onOpenSubmenu?.()}
    >
      <span className={styles.icon}><QualityIcon /></span>
      <span className={styles.label}><span className={styles.labelText}>Качество</span></span>
      <span className={styles.value}>{labels.get(quality) || quality}</span>
    </div>
  );
}
